import { useEffect, useState } from 'react'
import { ArrowTopRightOnSquareIcon, MegaphoneIcon, PhotoIcon, PlusIcon } from '@heroicons/react/20/solid'
import { useAuth } from '../../providers/auth/useAuth'
import {
  AdManagementService,
  type BatchAdResponse,
  type CampaignObjective,
  type CtaType_Input,
  type GeneratedAdResponse,
  type MetaConnectionRead,
} from '../../client'
import { IDLE_STATUSES, publishAds, type PublishStatuses } from '../../lib/promoteAds'
import {
  EMPTY_FORM,
  OBJECTIVE_GOALS,
  OBJECTIVE_GOAL_DEFAULT,
  countryLocation,
  type AdCreative,
  type ProgressStep,
  type PromotionForm,
} from '../../lib/adPromotion'
import { apiErrorMessage } from '../../lib/api'
import { openMetaAuthPopup } from '../../lib/metaAuth'
import posthog from '../../lib/posthog'
import Modal from '../ui/Modal'
import AccountPageSelector from './AccountPageSelector'
import AdCreativeConfig from './AdCreativeConfig'
import AdsetConfig from './AdsetConfig'
import CampaignObjectiveSelector from './CampaignObjectiveSelector'
import ExistingCampaignSelector from './ExistingCampaignSelector'
import MetaIcon from './MetaIcon'
import PromoteProgress from './PromoteProgress'

type Step = 'review' | 'campaign' | 'adset' | 'promoting'
type RegenField = 'primary_text' | 'headline' | 'description'

const SECONDARY =
  'rounded bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm ring-1 ring-slate-300 transition ring-inset hover:bg-slate-50'
const PRIMARY =
  'rounded bg-purple-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-purple-950 disabled:cursor-not-allowed disabled:opacity-50'
const CHIP = 'rounded bg-gray-100 px-2 py-0.5 text-xs'

function creativeFor(ad: GeneratedAdResponse, index: number, website: string): AdCreative {
  return {
    adName: `Adlovin Generated Ad ${index + 1}`,
    primaryText: ad.primary_text ?? '',
    headline: ad.headline ?? '',
    description: ad.description ?? '',
    ctaType: (ad.cta === 'WATCH_MORE' ? 'LEARN_MORE' : ad.cta) as CtaType_Input,
    ctaLink: website,
  }
}

type Props = {
  brandId: number
  ads: GeneratedAdResponse[]
  onClose: () => void
  onPromoted: () => void
  onGenerateMore?: () => void
}

export default function GeneratedAdFlow({ brandId, ads, onClose, onPromoted, onGenerateMore }: Props) {
  const { brand } = useAuth()
  const website = brand?.website ?? ''

  const [step, setStep] = useState<Step>('review')
  const [index, setIndex] = useState(0)
  const [connection, setConnection] = useState<MetaConnectionRead | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [choosingAccount, setChoosingAccount] = useState(false)
  const [currency, setCurrency] = useState('')
  const [newCampaign, setNewCampaign] = useState(false)
  const [form, setForm] = useState<PromotionForm>(() => ({
    ...EMPTY_FORM,
    targetingLocations: brand?.country ? [countryLocation(brand.country)] : [],
  }))
  const [creatives, setCreatives] = useState<AdCreative[]>(() => ads.map((ad, i) => creativeFor(ad, i, website)))
  const [regenerated, setRegenerated] = useState<Record<number, Set<RegenField>>>({})
  const [statuses, setStatuses] = useState<PublishStatuses>(IDLE_STATUSES)
  const [result, setResult] = useState<BatchAdResponse | null>(null)
  const [error, setError] = useState('')

  const adAccountId = connection?.last_used_ad_account_id ?? ''
  const newAdset = newCampaign || form.adsetMode === 'new'
  const needsAdsetStep = newAdset
  const canContinueCampaign = newCampaign
    ? Boolean(form.objective && form.campaignName)
    : Boolean(form.existingCampaignId) && (form.adsetMode === 'new' || Boolean(form.existingAdsetId))
  const canContinueAdset = form.budgetType === 'LIFETIME' ? Boolean(form.endTime) : true

  const progress: ProgressStep[] = [
    { key: 'campaign', label: newCampaign ? 'Create Campaign' : 'Use Existing Campaign', status: statuses.campaign },
    { key: 'adset', label: newAdset ? 'Create Ad Set' : 'Use Existing Ad Set', status: statuses.adset },
    { key: 'ad', label: `Create ${ads.length} Ad${ads.length === 1 ? '' : 's'}`, status: statuses.ad },
  ]

  const adsManagerUrl = result
    ? `https://adsmanager.facebook.com/adsmanager/manage/adsets?act=${adAccountId.replace('act_', '')}&selected_adset_ids=${result.adset_id}`
    : 'https://adsmanager.facebook.com'

  useEffect(() => {
    AdManagementService.getConnection(brandId)
      .then(setConnection)
      .catch(() => undefined)
  }, [brandId])

  useEffect(() => {
    if (!adAccountId) return

    AdManagementService.getAdAccounts(brandId)
      .then((accounts) => setCurrency(accounts.find(({ id }) => id === adAccountId)?.currency ?? ''))
      .catch(() => undefined)
  }, [brandId, adAccountId])

  function patch(changes: Partial<PromotionForm>) {
    setForm((current) => ({ ...current, ...changes }))
  }

  function changeObjective(objective: CampaignObjective) {
    const allowed = OBJECTIVE_GOALS[objective]
    patch({
      objective,
      optimizationGoal: allowed.includes(form.optimizationGoal) ? form.optimizationGoal : allowed[0],
    })
  }

  function markRegenerated(field: RegenField) {
    setRegenerated((current) => ({ ...current, [index]: new Set(current[index]).add(field) }))
  }

  async function connect() {
    setConnecting(true)
    setError('')
    try {
      await openMetaAuthPopup(brandId)
      const connected = await AdManagementService.getConnection(brandId)
      setConnection(connected)
      if (connected && !connected.last_used_ad_account_id) setChoosingAccount(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : apiErrorMessage(err))
    } finally {
      setConnecting(false)
    }
  }

  function goToCampaign() {
    setChoosingAccount(Boolean(connection) && !connection?.last_used_page_id)
    setStep('campaign')
  }

  async function promote() {
    setStep('promoting')
    setError('')
    setResult(null)

    try {
      const response = await publishAds({ brandId, ads, creatives, form, newCampaign, onProgress: setStatuses })
      setResult(response)
      posthog.capture('ads_promoted', {
        ad_count: ads.length,
        campaign_mode: newCampaign ? 'new' : 'existing',
      })
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  const title =
    step === 'review'
      ? 'Review Ad'
      : step === 'campaign'
        ? connection
          ? 'Publish to Meta'
          : 'Connect Meta to Publish'
        : step === 'adset'
          ? 'Ad Set Configuration'
          : 'Creating Ads...'

  const showBack = step === 'campaign' || step === 'adset' || (step === 'promoting' && Boolean(error))

  function goBack() {
    setStep(step === 'campaign' ? 'review' : 'campaign')
    if (step === 'promoting') setError('')
  }

  return (
    <Modal
      title={title}
      width="max-w-3xl"
      padded={false}
      onClose={onClose}
      onBack={showBack ? goBack : undefined}
    >

      {(step === 'campaign' || step === 'adset') && connection?.last_used_ad_account_name && (
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-50 px-6 py-2">
          <span className={CHIP}>
            <span className="font-medium text-gray-600">Ad Account: </span>
            <span className="text-gray-500">{connection.last_used_ad_account_name}</span>
          </span>
          {connection.last_used_page_name && (
            <span className={CHIP}>
              <span className="font-medium text-gray-600">Facebook Page: </span>
              <span className="text-gray-500">{connection.last_used_page_name}</span>
            </span>
          )}
          {step === 'campaign' && !choosingAccount && (
            <button
              type="button"
              onClick={() => setChoosingAccount(true)}
              className="text-xs text-indigo-600 hover:underline"
            >
              Switch
            </button>
          )}
        </div>
      )}

      {step === 'review' && (
        <>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">
              Ad Configuration <span className="ml-1 text-xs font-normal text-gray-400">[Ad/Creative level]</span>
            </h3>

            <AdCreativeConfig
              brandId={brandId}
              creative={creatives[index]}
              imageUrl={ads[index]?.image_url}
              brandName={brand?.name}
              brandWebsite={website}
              brandDescription={brand?.description ?? undefined}
              regenerated={regenerated[index] ?? new Set()}
              onChange={(creative) => setCreatives((current) => current.map((item, i) => (i === index ? creative : item)))}
              onRegenerated={markRegenerated}
            />

            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="flex items-center justify-center gap-2 overflow-x-auto pt-2 pb-1 [scrollbar-width:none]">
                {ads.map((ad, i) => (
                  <button
                    key={ad.image_url ?? i}
                    type="button"
                    onClick={() => setIndex(i)}
                    className={`size-14 shrink-0 overflow-hidden rounded-lg ${
                      index === i ? 'outline-2 outline-offset-2 outline-indigo-500' : ''
                    }`}
                  >
                    {ad.image_url ? (
                      <img src={ad.image_url} alt="" className="size-full object-contain" />
                    ) : (
                      <div className="flex size-full items-center justify-center bg-gray-100">
                        <PhotoIcon className="size-5 text-gray-400" />
                      </div>
                    )}
                  </button>
                ))}

                {onGenerateMore && (
                  <button
                    type="button"
                    onClick={onGenerateMore}
                    className="flex size-14 shrink-0 flex-col items-center justify-center gap-1 rounded-lg bg-indigo-50 transition-colors hover:bg-indigo-100"
                  >
                    <PlusIcon className="size-5 text-indigo-400" />
                    <span className="text-[10px] leading-none text-indigo-400">More</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-100 px-6 py-4">
            <p className="mb-3 text-xs text-gray-400">
              Your ad will be created and activated on Meta. You can manage it in Meta Ads Manager.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className={SECONDARY}>
                Cancel
              </button>
              <button type="button" onClick={goToCampaign} className={PRIMARY}>
                Continue
              </button>
            </div>
          </div>
        </>
      )}

      {step === 'campaign' && !connection && (
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-16">
          <MetaIcon className="mb-4 size-12 text-[#1877F2]" />
          <p className="mb-2 text-base font-semibold text-gray-800">Connect your Meta account to publish</p>
          <p className="mb-6 max-w-sm text-center text-sm text-gray-500">
            Link your Meta Business account to publish this ad to Facebook and Instagram.
          </p>
          <button
            type="button"
            disabled={connecting}
            onClick={() => void connect()}
            className="inline-flex items-center gap-2 rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 px-4 py-2 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            <MetaIcon className="size-4" />
            {connecting ? 'Waiting for Meta…' : 'Connect Meta Account'}
          </button>
          {error && <p className="mt-4 text-xs text-red-500">{error}</p>}
        </div>
      )}

      {step === 'campaign' && connection && choosingAccount && (
        <div className="flex-1 overflow-y-auto p-6">
          <AccountPageSelector
            brandId={brandId}
            initialAdAccountId={connection.last_used_ad_account_id}
            initialPageId={connection.last_used_page_id}
            onBack={() => setChoosingAccount(false)}
            onSaved={(updated) => {
              setConnection(updated)
              setChoosingAccount(false)
            }}
          />
        </div>
      )}

      {step === 'campaign' && connection && !choosingAccount && (
        <>
          <div className="flex-1 space-y-4 overflow-y-auto p-6">
            <ExistingCampaignSelector
              brandId={brandId}
              adAccountId={adAccountId}
              newCampaign={newCampaign}
              campaignId={form.existingCampaignId}
              newAdset={form.adsetMode === 'new'}
              adsetId={form.existingAdsetId}
              onNewCampaign={() => {
                setNewCampaign(true)
                patch({ existingCampaignId: '', existingAdsetId: '', adsetMode: 'new' })
              }}
              onSelectCampaign={(campaign) => {
                setNewCampaign(false)
                patch({
                  existingCampaignId: campaign?.id ?? '',
                  existingCampaignName: campaign?.name ?? '',
                  existingAdsetId: '',
                  adsetMode: 'existing',
                  optimizationGoal: campaign?.objective
                    ? (OBJECTIVE_GOAL_DEFAULT[campaign.objective as CampaignObjective] ?? 'IMPRESSIONS')
                    : form.optimizationGoal,
                })
              }}
              onNewAdset={() => patch({ existingAdsetId: '', existingAdsetName: '', adsetMode: 'new' })}
              onSelectAdset={(adset) =>
                patch({
                  existingAdsetId: adset?.id ?? '',
                  existingAdsetName: adset?.name ?? '',
                  adsetMode: 'existing',
                })
              }
            />

            {newCampaign && (
              <div className="mt-6 border-t border-gray-100 pt-6">
                <CampaignObjectiveSelector
                  objective={form.objective}
                  campaignName={form.campaignName}
                  onObjectiveChange={changeObjective}
                  onCampaignNameChange={(campaignName) => patch({ campaignName })}
                />
              </div>
            )}

            <p className="mt-4 text-xs text-gray-400">Your campaign will be created and activated.</p>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
            <button type="button" onClick={() => setStep('review')} className={SECONDARY}>
              Back
            </button>
            <button
              type="button"
              disabled={!canContinueCampaign}
              onClick={() => (needsAdsetStep ? setStep('adset') : void promote())}
              className={PRIMARY}
            >
              Continue
            </button>
          </div>
        </>
      )}

      {step === 'adset' && (
        <>
          <div className="flex-1 overflow-y-auto p-6">
            <AdsetConfig
              brandId={brandId}
              form={form}
              newCampaign={newCampaign}
              currency={currency}
              onChange={patch}
            />
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
            <button type="button" onClick={() => setStep('campaign')} className={SECONDARY}>
              Back
            </button>
            <button type="button" disabled={!canContinueAdset} onClick={() => void promote()} className={PRIMARY}>
              Continue
            </button>
          </div>
        </>
      )}

      {step === 'promoting' && (
        <>
          <div className="flex-1 overflow-y-auto p-6">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">{result ? 'Done' : 'Creating Ads...'}</h3>
            <PromoteProgress steps={progress} error={error} />

            {result && (
              <div className="mt-6 overflow-hidden rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 border-b border-gray-200 bg-indigo-50 px-4 py-3">
                  <MegaphoneIcon className="size-5 shrink-0 text-indigo-500" />
                  <p className="text-sm font-semibold text-gray-900">
                    {ads.length} ad{ads.length === 1 ? '' : 's'} created and activated
                  </p>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MetaIcon className="size-4 shrink-0 text-[#1877F2]" />
                    <span>View your ads in Meta Ads Manager</span>
                  </div>
                  <a
                    href={adsManagerUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-3 flex shrink-0 items-center gap-1.5 text-sm font-medium text-indigo-600 transition-colors hover:text-indigo-700"
                  >
                    Open Ads Manager
                    <ArrowTopRightOnSquareIcon className="size-3.5" />
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-6 py-4">
            {error && (
              <button
                type="button"
                onClick={() => {
                  setStep('campaign')
                  setError('')
                }}
                className={SECONDARY}
              >
                Try Again
              </button>
            )}
            {result && (
              <button type="button" onClick={onPromoted} className={PRIMARY}>
                Done
              </button>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}
